import { __assign, __read } from "tslib";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { noop, useDrag } from '../Drawer.utils';
import { cnDrawerContent, cnDrawerCurtain, cnDrawerDragObserver, cnDrawerHandle, cnDrawerOverlay, cnDrawerTitle, } from '../Drawer.const';
function getSpringTransform(_a) {
    var axis = _a.axis, springValue = _a.springValue, inverted = _a.inverted;
    if (springValue === 1) {
        return null;
    }
    return axis === 'x'
        ? "translate3d(" + (1 - springValue) * 100 * inverted + "%,0,0)"
        : "translate3d(0," + (1 - springValue) * 100 * inverted + "%,0)";
}
/**
 * Компонент содержимого шторки.
 *
 * При использовании Popup мы не знаем в какой конкретно
 * момент содержимое монтируется/размонтируется, если
 * прокидывать его напрямую без обёрток.
 */
export var DrawerContent = function (_a) {
    var dragDisabled = _a.dragDisabled, visible = _a.visible, _b = _a.onClose, onClose = _b === void 0 ? noop : _b, _c = _a.onCloseEnd, onCloseEnd = _c === void 0 ? noop : _c, springValue = _a.springValue, _d = _a.direction, direction = _d === void 0 ? 'bottom' : _d, maxSize = _a.maxSize, titleComponent = _a.titleComponent, children = _a.children, setSpringDisabled = _a.setSpringDisabled, setProgress = _a.setProgress;
    var contentRef = useRef(null);
    var _e = __read(useState(false), 2), closing = _e[0], setClosing = _e[1];
    var axis = direction === 'bottom' ? 'y' : 'x';
    var inverted = direction === 'left' ? -1 : 1;
    var springOpacity = Math.max(springValue, 0);
    var springTransform = getSpringTransform({ axis: axis, springValue: springValue, inverted: inverted });
    var curtainStyle = useMemo(function () {
        var _a;
        return (__assign(__assign({}, (springTransform ? { transform: springTransform } : {})), (maxSize && (_a = {}, _a[axis === 'x' ? 'maxWidth' : 'maxHeight'] = maxSize, _a))));
    }, [springTransform, maxSize, axis]);
    useEffect(function () {
        if (closing && springValue === 0) {
            onCloseEnd();
            setClosing(false);
        }
    }, [closing, springValue, onCloseEnd]);
    var _onClose = useCallback(function () {
        setClosing(true);
        onClose();
    }, [onClose]);
    /**
     * Обработчик drag событий с корневого DOM элемента шторки
     */
    var dragProps = useDrag(function (dragState) {
        if (!visible || !contentRef.current)
            return;
        var _a = dragState.velocity, vx = _a.x, vy = _a.y, _b = dragState.movement, mx = _b.x, my = _b.y, first = dragState.first, last = dragState.last, data = dragState.data, event = dragState.event;
        var drawerSize = axis === 'x' ? contentRef.current.clientWidth : contentRef.current.clientHeight;
        var movement = inverted * (axis === 'x' ? mx : my);
        var velocity = inverted * (axis === 'x' ? vx : vy);
        if (first) {
            data.isTargetUnderContent = contentRef.current.contains(event.target);
            data.initialScrollPosition = contentRef.current.scrollTop;
            data.isScrolled = data.initialScrollPosition !== 0;
        }
        else {
            data.isScrolled = data.isScrolled || data.initialScrollPosition - contentRef.current.scrollTop < 0;
        }
        // ничего не делаем когда жест происходит одновременно с проскроллом
        // или если шторка в статичном состоянии
        // @see SERP-107544
        if (dragDisabled || (data.isTargetUnderContent && data.isScrolled)) {
            return;
        }
        // жест завершен, возвращаем шторку в открытое положение, если
        // скорость была недостаточной и, закрываем если наоборот
        if (last) {
            setSpringDisabled(false);
            if (Math.abs(velocity) >= 0.1) {
                return velocity > 0 ? _onClose() : setProgress(1);
            }
            else if (movement / drawerSize >= 0.3) {
                return _onClose();
            }
            return setProgress(1);
        }
        if (movement > 0) {
            setSpringDisabled(true);
            var progress = Math.max(0, 1 - movement / drawerSize);
            if (progress === 0) {
                return _onClose();
            }
            return setProgress(progress);
        }
    }).dragProps;
    return (React.createElement("div", __assign({ className: cnDrawerDragObserver }, dragProps),
        React.createElement("div", { className: cnDrawerOverlay, style: { opacity: springOpacity }, onClick: function () { return _onClose(); } }),
        React.createElement("div", { className: cnDrawerCurtain, style: curtainStyle },
            React.createElement("div", { className: cnDrawerHandle, style: { opacity: springOpacity } }),
            titleComponent && React.createElement("div", { className: cnDrawerTitle }, titleComponent),
            React.createElement("div", { className: cnDrawerContent, ref: contentRef }, children))));
};
